import { useState, useEffect, useCallback } from 'react';
import { getListCountry } from 'services/core/services';

export type Country = {
  countryToSave: string;
  country: string;
  name: string;
  id: string;
};

type CountryResponse = {
  data?: Array<{
    country: string;
    citizenship: string;
    countryToSave: string;
    ddi: string;
    iso2: string;
    iso3: string;
  }>;
  success: boolean;
};

const STORAGE_KEY = '@smls:validate-actions:countries';

export const useCountries = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Função para buscar países da API
  const fetchCountries = useCallback(async (forceUpdate = false) => {
    try {
      setLoading(true);

      // Verifica cache se não for forçar atualização
      if (!forceUpdate) {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached) as Country[];
          setCountries(parsed);
          setLoading(false);
          return;
        }
      }

      // Simulação da sua função getListCountry
      const response: CountryResponse = (await getListCountry()) as CountryResponse;

      if (!response.success) {
        throw new Error('[getListCountry] failed on return countries.');
      }

      const formattedCountries = (response?.data || []).map((el) => ({
        ...el,
        name: el.country,
        id: el.country,
      }));

      localStorage.setItem(STORAGE_KEY, JSON.stringify(formattedCountries));
      setCountries(formattedCountries);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch countries'));
      console.error('Error fetching countries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getCachedCountries = useCallback(() => {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      return JSON.parse(cached) as Country[];
    }
    return null;
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  return {
    countries,
    loading,
    error,
    fetchCountries,
    getCachedCountries,
  };
};
